import { showSubPage, setActiveStep } from './steps';
import {
  MATERIAL_FORMATS,
  materialIdAttr,
  loadMaterials,
  clearMaterialPreview,
  clearUploadErrors,
  initMaterialPanel,
} from './materials';
import { populateAdSetReview } from './review';
import { submitCreateAdSet, readGeoLocations } from './submit';
import {
  initAdSetAudience,
  resetAdSetAudienceStep,
  restoreAdSetAudienceStep,
  handleAudienceNext,
} from './audience';
import Choices from 'choices.js/assets/scripts/dist/choices.min.js';
import { initGeoLocationDropdowns, refreshGeoSummaries } from '../geo_locations';
import { bindScheduleWindow, syncScheduleWindow } from '../schedule_window';

interface CampaignOption {
  id: string;
  name: string;
  geo_locations?: string[];
}

const $body = (): JQuery => $('#ad-set-modal-body');

let campaignChoices: any = null;
let campaignInitEl: HTMLSelectElement | null = null;
let campaignGeoById: Record<string, string[]> = {};

function geoCheckboxes(): HTMLInputElement[] {
  return Array.from(document.querySelectorAll<HTMLInputElement>(
    '#ad-set-modal-body [data-role="adset-geo-location"] input[type="checkbox"]'
  ));
}

function selectedCampaignId(): string {
  const el = document.querySelector<HTMLSelectElement>(
    '#ad-set-modal-body [data-role="adset-campaign-select"]'
  );
  return el?.value ?? '';
}

function resetGeoCheckboxes(): void {
  geoCheckboxes().forEach(cb => {
    cb.checked = false;
    cb.disabled = true;
  });
  refreshGeoSummaries();
}

function applyCampaignGeoRestriction(): void {
  const boxes = geoCheckboxes();
  if (!boxes.length) return;

  const campaignId = selectedCampaignId();
  if (!campaignId) {
    resetGeoCheckboxes();
    return;
  }

  const allowed = campaignGeoById[campaignId] ?? [];
  const restricted = allowed.length > 0;

  boxes.forEach(cb => {
    const isAllowed = allowed.includes(cb.value);
    cb.disabled = restricted ? !isAllowed : false;
    cb.checked = restricted ? isAllowed : cb.value === 'SE';
  });
  refreshGeoSummaries();
}

function loadCampaignOptions(): void {
  const selectEl = document.querySelector<HTMLSelectElement>(
    '#ad-set-modal-body [data-role="adset-campaign-select"]'
  );
  if (!selectEl) return;

  if (campaignChoices && campaignInitEl !== selectEl) {
    try { campaignChoices.destroy(); } catch { /* already gone */ }
    campaignChoices = null;
    campaignGeoById = {};
  }

  if (!campaignChoices) {
    campaignChoices = new Choices(selectEl, {
      searchEnabled: true,
      searchPlaceholderValue: 'Search campaigns…',
      shouldSort: false,
      itemSelectText: '',
      placeholder: true,
      placeholderValue: 'Choose a campaign…',
    });
    campaignInitEl = selectEl;
    selectEl.addEventListener('change', applyCampaignGeoRestriction);
  }

  campaignChoices.setChoices(
    [{ value: '', label: 'Loading campaigns…', disabled: true, selected: true }],
    'value', 'label', true
  );
  campaignChoices.disable();

  $.ajax({
    url: '/meta_apis/ad_sets/campaign_options',
    method: 'GET',
    success: function(response: { success: boolean; data: CampaignOption[] }) {
      campaignGeoById = {};
      (response.data ?? []).forEach(c => { campaignGeoById[c.id] = c.geo_locations ?? []; });
      const choices = [
        { value: '', label: 'Choose a campaign…', placeholder: true, selected: true },
        ...(response.data ?? []).map(c => ({ value: c.id, label: c.name })),
      ];
      campaignChoices.setChoices(choices, 'value', 'label', true);
      applyCampaignGeoRestriction();
    },
    error: function() {
      campaignChoices.setChoices(
        [{ value: '', label: 'Failed to load campaigns', disabled: true, selected: true }],
        'value', 'label', true
      );
    },
    complete: function() {
      campaignChoices.enable();
    },
  });
}

function resetAdSetModal($modal: JQuery): void {
  ($modal.find('form.ad-set-form')[0] as HTMLFormElement | undefined)?.reset();
  resetGeoCheckboxes();
  syncScheduleWindow('#adset-start-time', '#adset-end-time');

  MATERIAL_FORMATS.forEach(fmt => $body().removeAttr(materialIdAttr(fmt)));

  $modal.find('.material-panel').each(function() {
    const $panel = $(this);
    $panel.find('.material-tab').removeClass('active');
    $panel.find('.material-tab[data-tab="library"]').addClass('active');
    $panel.find('.material-tab-pane').addClass('d-none');
    $panel.find('[data-tab-pane="library"]').removeClass('d-none');
    $panel.find('[data-role="material-name"]').val('');
    const fileInput = $panel.find('[data-role="file-input"]')[0] as HTMLInputElement | undefined;
    if (fileInput) fileInput.value = '';
    $panel.find('[data-role="materials-grid"]').empty();
    $panel.find('[data-role="materials-pagination"]').empty();
    clearMaterialPreview($panel);
    clearUploadErrors($panel);
  });

  $modal.find('[data-role="adset-summary-details"]').empty();
  $modal.find('[data-role="adset-summary-materials"]').empty();

  resetAdSetAudienceStep($modal);

  showSubPage('#adset-page-audience');
  setActiveStep('#adset-step-audience');
  $('#adset-next-btn').removeClass('save-audience').text('Next');
  $('#adset-back-btn').addClass('d-none');

  loadCampaignOptions();
}

function advanceToDetails(): void {
  showSubPage('#adset-page-details');
  setActiveStep('#adset-step-details');
  $('#adset-back-btn').removeClass('d-none');
  $('#adset-next-btn').removeClass('save-audience').text('Next');
}

function advanceToMaterials(): void {
  showSubPage('#adset-page-materials');
  setActiveStep('#adset-step-materials');
  $('#adset-back-btn').removeClass('d-none');
  $('#adset-next-btn').text('Next');
  MATERIAL_FORMATS.forEach(fmt => loadMaterials(fmt, 1));
}

function advanceToSummary(): void {
  populateAdSetReview();
  showSubPage('#adset-page-summary');
  setActiveStep('#adset-step-summary');
  $('#adset-back-btn').removeClass('d-none');
  $('#adset-next-btn').text('Create Ad Set');
}

function validateDetailsStep(): boolean {
  const form = $('#ad-set-modal-body form.ad-set-form')[0] as HTMLFormElement | undefined;
  if (form && typeof form.reportValidity === 'function' && !form.reportValidity()) return false;

  if (readGeoLocations().length === 0) {
    window.alert('Select a geographic location.');
    return false;
  }

  return true;
}

function validateMaterialsStep(): boolean {
  const squareId = $body().attr(materialIdAttr('square'));
  const storyId = $body().attr(materialIdAttr('story'));
  if (!squareId && !storyId) {
    window.alert('Select or upload at least one material (square or story).');
    return false;
  }
  return true;
}

export function initAdSet(): void {
  const $modal = $('#adSetModal');
  if (!$modal.length) return;

  $modal
    .off('show.bs.modal.resetAdSet hidden.bs.modal.resetAdSet')
    .on('show.bs.modal.resetAdSet hidden.bs.modal.resetAdSet', function() {
      resetAdSetModal($(this));
    });

  $modal.find('.material-panel').each(function() { initMaterialPanel($(this)); });

  initAdSetAudience();
  initGeoLocationDropdowns();
  bindScheduleWindow('adsetSchedule', '#adset-start-time', '#adset-end-time');

  $('#adset-next-btn').off('click').on('click', function() {
    const $btn = $(this);

    if (!$('#adset-page-audience').hasClass('d-none')) {
      handleAudienceNext(advanceToDetails);
      return;
    }

    if (!$('#adset-page-details').hasClass('d-none')) {
      if (!validateDetailsStep()) return;
      advanceToMaterials();
      return;
    }

    if (!$('#adset-page-materials').hasClass('d-none')) {
      if (!validateMaterialsStep()) return;
      advanceToSummary();
      return;
    }

    if (!$('#adset-page-summary').hasClass('d-none')) {
      submitCreateAdSet($btn);
      return;
    }
  });

  $('#adset-back-btn').off('click').on('click', function() {
    if (!$('#adset-page-summary').hasClass('d-none')) {
      showSubPage('#adset-page-materials');
      setActiveStep('#adset-step-materials');
      $('#adset-next-btn').text('Next');
      return;
    }

    if (!$('#adset-page-materials').hasClass('d-none')) {
      showSubPage('#adset-page-details');
      setActiveStep('#adset-step-details');
      $('#adset-next-btn').text('Next');
      return;
    }

    if (!$('#adset-page-details').hasClass('d-none')) {
      showSubPage('#adset-page-audience');
      setActiveStep('#adset-step-audience');
      $('#adset-back-btn').addClass('d-none');
      $('#adset-next-btn').text('Next');
      restoreAdSetAudienceStep();
      return;
    }
  });

  $(document).off('click.adsetEditStep').on('click.adsetEditStep', '#adset-page-summary [data-edit-step]', function() {
    const target = ($(this).data('edit-step') as string) ?? '';
    $('#adset-next-btn').text('Next');
    if (target === 'audience') {
      showSubPage('#adset-page-audience');
      setActiveStep('#adset-step-audience');
      $('#adset-back-btn').addClass('d-none');
      $('#adset-next-btn').removeClass('save-audience');
      restoreAdSetAudienceStep();
      return;
    }
    if (target === 'details') {
      showSubPage('#adset-page-details');
      setActiveStep('#adset-step-details');
      $('#adset-back-btn').removeClass('d-none');
      return;
    }
    if (target === 'materials') {
      showSubPage('#adset-page-materials');
      setActiveStep('#adset-step-materials');
      $('#adset-back-btn').removeClass('d-none');
      return;
    }
  });
}
